import { useEffect, useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PageMeta from '@/components/base/PageMeta';
import { brandInfo, testimonials, achievements } from '@/mocks/about';
import { siteInfo } from '@/mocks/homepage';

function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt')?.toString().trim() ?? '';
    if (honeypot) {
      setStatus('success');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9pmmo1n2trhb35gvi20', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries()).filter(([k]) => k !== 'website_alt').map(([k, v]) => [k, v.toString()])
        ).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed?.meta as Record<string, unknown>)?.message
          ?? (parsed?.meta as Record<string, unknown>)?.detail
          ?? (parsed?.message as string)
          ?? responseText
          ?? '提交失败，请稍后重试';
        setErrorMsg(String(serverMsg));
        setStatus('error');
      }
    } catch {
      setErrorMsg('网络错误，请检查网络后重试');
      setStatus('error');
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
          您的姓名 <span className="text-red-500">*</span>
        </label>
        <input
          id="contact-name"
          name="name"
          type="text"
          required
          placeholder="请输入您的姓名"
          className="w-full px-4 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all duration-200"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
          电子邮箱 <span className="text-red-500">*</span>
        </label>
        <input
          id="contact-email"
          name="email"
          type="email"
          required
          placeholder="请输入您的邮箱地址"
          className="w-full px-4 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all duration-200"
        />
      </div>
      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
          主题
        </label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          placeholder="请输入咨询主题"
          className="w-full px-4 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all duration-200"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
          留言内容 <span className="text-red-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={500}
          placeholder="请输入您的留言内容（500字以内）"
          className="w-full px-4 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all duration-200 resize-none"
        />
        <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
      </div>
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="honeypot-field"
      />
      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full px-6 py-3 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-medium text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
      >
        {status === 'loading' ? (
          <>
            <i className="ri-loader-4-line animate-spin mr-2" />
            提交中...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line mr-2" />
            发送留言
          </>
        )}
      </button>
      {status === 'success' && (
        <div className="p-4 rounded-lg bg-accent-50 border border-accent-100">
          <p className="text-sm text-accent-700 flex items-center gap-2">
            <i className="ri-check-line" />
            留言已发送，我们会尽快回复您！
          </p>
        </div>
      )}
      {status === 'error' && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-100">
          <p className="text-sm text-red-700 flex items-center gap-2">
            <i className="ri-error-warning-line" />
            {errorMsg}
          </p>
        </div>
      )}
    </form>
  );
}

const aboutSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://98tang.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
    {
      '@type': 'AboutPage',
      name: '关于我们 - 98堂 入口',
      description: brandInfo.description,
      url: 'https://98tang.com/about',
    },
    {
      '@type': 'Organization',
      name: brandInfo.name,
      url: 'https://98tang.com',
      description: brandInfo.description,
      founder: { '@type': 'Person', name: '98堂团队' },
      foundingDate: brandInfo.foundedYear,
      email: siteInfo.email,
      telephone: siteInfo.phone,
      address: {
        '@type': 'PostalAddress',
        streetAddress: siteInfo.address,
        addressCountry: 'CN',
      },
    },
  ],
};

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <PageMeta
        title="关于我们 - 98堂 入口"
        description={brandInfo.description}
        keywords="98堂 入口,关于98堂,98堂品牌介绍,98堂联系方式"
        canonicalUrl="https://98tang.com/about"
        schema={aboutSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-secondary-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <div className="bg-secondary-50 border-b border-secondary-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              关于我们
            </h1>
            <p className="text-sm text-foreground-500 max-w-2xl">
              了解98堂入口导航的品牌故事与发展历程
            </p>
          </div>
        </div>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium mb-4">
                  品牌故事
                </span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  {brandInfo.mission}
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  {brandInfo.story.split('\n\n').map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                  {achievements.map((a, i) => (
                    <div key={i} className="text-center p-4 rounded-lg bg-secondary-50">
                      <div className="text-xl md:text-2xl font-heading font-bold text-primary-500">
                        {a.value}
                      </div>
                      <div className="text-xs text-foreground-500 mt-1">{a.label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-lg overflow-hidden h-[400px]">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20modern%20professional%20office%20team%20working%20collaboratively%20around%20a%20table%2C%20warm%20orange%20and%20neutral%20tones%2C%20clean%20minimal%20workspace%20with%20natural%20lighting%2C%20focused%20atmosphere%2C%20corporate%20but%20friendly%20style%2C%20abstract%20touch%20with%20soft%20gradients%2C%20high%20quality%20editorial%20photography&width=800&height=600&seq=about-brand-98tang&orientation=landscape"
                  alt="98堂团队"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-secondary-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-50 text-accent-600 text-xs font-medium mb-4">
                核心价值观
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                我们的理念
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                以用户为中心，以技术为驱动，持续提供优质的入口导航服务
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {brandInfo.values.map((v, i) => (
                <div key={i} className="p-6 rounded-lg bg-background-50 border border-secondary-200/60 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${v.icon} text-primary-600 text-xl`} />
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-500">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-accent-50 text-accent-600 text-xs font-medium mb-4">
                用户口碑
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                用户评价
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                来自真实用户的使用反馈
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 rounded-lg bg-background-50 border border-secondary-200/60">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-amber-400 text-sm" />
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 pt-3 border-t border-secondary-100">
                    <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-accent-700">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-secondary-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-xs font-medium mb-4">
                联系我们
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                在线留言
              </h2>
              <p className="text-sm text-foreground-500">
                如果您有任何问题或建议，欢迎随时与我们联系
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              <div className="md:col-span-2 flex flex-col gap-5">
                <div className="p-5 rounded-lg bg-background-50 border border-secondary-200/60">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center mb-3">
                    <i className="ri-mail-line text-primary-600 text-lg" />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                  <a href={`mailto:${siteInfo.email}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 cursor-pointer">
                    {siteInfo.email}
                  </a>
                </div>
                <div className="p-5 rounded-lg bg-background-50 border border-secondary-200/60">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                    <i className="ri-phone-line text-accent-600 text-lg" />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h4>
                  <a href={`tel:${siteInfo.phone}`} className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200 cursor-pointer">
                    {siteInfo.phone}
                  </a>
                  <p className="text-xs text-foreground-400 mt-1">工作日 9:00 - 18:00</p>
                </div>
                <div className="p-5 rounded-lg bg-background-50 border border-secondary-200/60">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-3">
                    <i className="ri-map-pin-line text-accent-600 text-lg" />
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">办公地址</h4>
                  <p className="text-sm text-foreground-500">{siteInfo.address}</p>
                </div>
              </div>
              <div className="md:col-span-3">
                <div className="p-6 rounded-lg bg-background-50 border border-secondary-200/60">
                  <ContactForm />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}